let marameWishlistRefreshQueued = false;
function queueMarameWishlistRefresh() {
  if (typeof window === 'undefined' || marameWishlistRefreshQueued) return;
  // The installed provider initializes these before its own collection scan.
  // If it is still loading, its initial scan will pick up the current cards.
  if (typeof window.iWish?.checkIwishColl !== 'function' || !window.iwishAddProxy || !window.changeDetect) return;
  marameWishlistRefreshQueued = true;
  queueMicrotask(() => {
    marameWishlistRefreshQueued = false;
    if (!document.querySelector('marame-wishlist-action .iWishAddColl.iwishcheck')) return;
    try {
      // Use the provider's idempotent filter-refresh method, after connected
      // cards have their real product and variant IDs. It owns saved state.
      window.iWish.checkIwishColl();
    } catch (_) {
      // Preserve the existing honest unavailable state if the provider fails.
    }
  });
}

// A saved provider variant identifies a colour, not evidence of an intentional
// size choice. Only a verified size-free item may use the provider's direct add.
if (!customElements.get('marame-wishlist-selection')) {
  customElements.define('marame-wishlist-selection', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.records = new WeakMap();
      this.products = new Map();
      this.guard = (event) => {
        if (event.target.closest?.('#iwish-drawer-root .addToCart-btn')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        const control = event.target.closest?.('.iwish-product-form, .iwishBuyBtn, #iwish-drawer-root .add_to_cart');
        const row = control?.closest('.iwishItem, .iwishProduct-row');
        if (!row) return;
        const record = this.records.get(row);
        const identity = this.identity(row);
        const input = row.querySelector('.iwish-product-form [name="id"]');
        if (record?.sizeFree && record.key === identity?.key && (!input || input.value === identity.variant)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.scan();
        row.querySelector('[data-marame-wishlist-select]')?.focus();
      };
      document.addEventListener('click', this.guard, {capture:true, signal:this.events.signal});
      document.addEventListener('submit', this.guard, {capture:true, signal:this.events.signal});
      this.observer = new MutationObserver((changes) => {
        if (!changes.some((change) => change.target.closest?.('.iwishWrapper, #iwish-drawer-root') ||
          [...change.addedNodes].some((node) => node.nodeType === 1 && (node.matches('.iwishWrapper, #iwish-drawer-root') || node.querySelector('.iwishWrapper, #iwish-drawer-root'))))) return;
        if (this.queued) return;
        this.queued = true;
        queueMicrotask(() => { this.queued = false; if (this.events) this.scan(); });
      });
      this.observer.observe(document.body, {subtree:true, childList:true, attributes:true, attributeFilter:['href', 'id']});
      this.scan();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.observer?.disconnect();
    }
    identity(row) {
      try {
        const anchor = row.querySelector('.iwishMeta-title a, .product-title a[data-handle]');
        if (!anchor) return null;
        const root = new URL(window.Shopify?.routes?.root || '/', location.origin);
        const source = new URL(anchor.getAttribute('href'), location.origin);
        const handle = anchor.dataset.handle || source.pathname.match(/\/products\/([^/]+)\/?$/)?.[1];
        const variant = source.searchParams.get('variant') || row.id.match(/^product_var(\d+)$/)?.[1];
        if (source.origin !== location.origin || root.origin !== location.origin || !/^[\w%-]+$/.test(handle || '') || !/^\d+$/.test(variant || '')) return null;
        const url = new URL(`${root.pathname.replace(/\/?$/, '/')}products/${handle}`, location.origin);
        url.searchParams.set('variant', variant);
        const productId = row.querySelector('.iwish-product-form')?.dataset.product || '';
        return {key:[url.pathname, variant, productId].join('|'), url, variant, productId};
      } catch { return null; }
    }
    async product(identity) {
      const endpoint = `${identity.url.pathname}.js`;
      if (!this.products.has(endpoint)) {
        // Public, locale-aware Shopify product metadata only. No wishlist or cart writes.
        this.products.set(endpoint, fetch(endpoint, {credentials:'same-origin', headers:{Accept:'application/json'}})
          .then((response) => { if (!response.ok) throw new Error('Product unavailable'); return response.json(); })
          .catch(() => null));
      }
      return this.products.get(endpoint);
    }
    assess(product, identity) {
      if (!product || (identity.productId && String(product.id) !== identity.productId)) return null;
      const variant = product.variants?.find((item) => String(item.id) === identity.variant);
      if (!variant) return null;
      const normal = (value) => String(value || '').trim().toLowerCase();
      const sizeName = /^(size|sizes|taille|tamaño|talla|tamanho|größe|groesse|taglia|maat|storlek|beden|dydis|rozmiar|veličina|velicina|μέγεθος|maint|サイズ|尺码)$/;
      const colourName = /^(colou?r|couleur|cor|farbe|colore|kleur|färg|renk|spalva|kolor|boja|χρώμα|lliw|カラー|颜色)$/;
      const oneSize = /^(one[ -]?size( fits all)?|os|o\/s|osfa|taille unique|talla única|tamanho único|einheitsgröße|taglia unica|unica|unico|onesize|tek beden|vienas dydis|uniwersalny|univerzalna|ένα μέγεθος|un maint|フリー|ワンサイズ|均码)$/;
      const options = product.options || [];
      if (!options.length) return null;
      const sizeIndexes = options.map((option, index) => sizeName.test(normal(option.name)) ? index : -1).filter((index) => index >= 0);
      const sizeFree = options.every((option, index) => {
        const values = option.values || [...new Set(product.variants.map((item) => item.options?.[index]))];
        if (!values.length || values.some((value) => typeof value !== 'string' || !value.trim())) return false;
        if (colourName.test(normal(option.name))) return true;
        if (sizeName.test(normal(option.name))) return values.every((value) => oneSize.test(normal(value)));
        return options.length === 1 && values.length === 1 && normal(values[0]) === 'default title';
      });
      const description = variant.options?.filter((value, index) => !sizeIndexes.includes(index) && normal(value) !== 'default title').join(' / ') || '';
      return {sizeFree, description, hasSize:sizeIndexes.length > 0};
    }
    scan() {
      document.querySelectorAll('.iwishWrapper .iwishItem, #iwish-drawer-root .iwishProduct-row').forEach((row) => {
        const identity = this.identity(row);
        let record = this.records.get(row);
        if (!record || record.key !== identity?.key) {
          record = {key:identity?.key, sizeFree:false, result:null};
          this.records.set(row, record);
          if (identity) this.product(identity).then((product) => {
            if (!this.events || !row.isConnected || this.records.get(row) !== record || this.identity(row)?.key !== record.key) return;
            record.result = this.assess(product, identity);
            record.sizeFree = record.result?.sizeFree === true;
            this.render(row, record, identity);
          });
        }
        this.render(row, record, identity);
      });
    }
    render(row, record, identity) {
      const form = row.querySelector('.iwish-product-form');
      const button = row.querySelector('.iwishBuyBtn, .add_to_cart');
      const quantity = row.querySelector('.iwish-cartQty');
      [form, button, quantity].filter(Boolean).forEach((element) => element.toggleAttribute('data-marame-size-free', record.sizeFree));
      // Also block direct form submission while product data is unresolved/sized.
      const idInput = form?.querySelector('[name="id"]');
      if (idInput) {
        if (!record.sizeFree && !idInput.disabled) { idInput.disabled = true; idInput.dataset.marameSizeGuard = 'true'; }
        else if (record.sizeFree && idInput.dataset.marameSizeGuard === 'true') { idInput.disabled = false; delete idInput.dataset.marameSizeGuard; }
      }
      const variantText = row.querySelector('.iwishMeta-variant-title, .product-variant');
      if (variantText) {
        variantText.toggleAttribute('data-marame-variant-checked', Boolean(record.result));
        if (record.result && !record.sizeFree && variantText.textContent !== record.result.description) variantText.textContent = record.result.description;
      }
      let link = row.querySelector('[data-marame-wishlist-select]');
      if (record.sizeFree || !identity) { link?.remove(); return; }
      if (!link) {
        link = document.createElement('a');
        link.className = 'marame-wishlist-select';
        link.setAttribute('data-marame-wishlist-select', '');
        link.setAttribute('data-marame-quick-add', '');
        const parent = row.querySelector('.iwishMeta, .cart-button');
        if (!parent) return;
        parent.append(link);
      }
      if (link.getAttribute('href') !== identity.url.href) link.setAttribute('href', identity.url.href);
      const label = record.result && !record.result.hasSize ? this.dataset.optionsLabel : this.dataset.sizeLabel;
      if (link.textContent !== label) link.textContent = label;
    }
  });
}

if (!customElements.get('marame-wishlist-action')) {
  customElements.define('marame-wishlist-action', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (!this.querySelector('.marame-wishlist-button')) {
        const source = document.getElementById('MarameWishlistTemplate')?.content.firstElementChild;
        if (!source) return;
        for (const [key, value] of Object.entries(source.dataset)) {
          if (!(key in this.dataset)) this.dataset[key] = value;
        }
        this.append(...Array.from(source.cloneNode(true).childNodes));
      }
      this.button = this.querySelector('.marame-wishlist-button');
      this.feedback = this.querySelector('[data-wishlist-feedback]');
      this.fallback = this.querySelector('[data-wishlist-fallback]');
      this.productInfo = this.closest('product-info');
      this.pending = false;
      this.initialized = true;
      const signedIn = this.dataset.customerSignedIn === 'true';
      this.button.classList.toggle('iWishAdd', signedIn && this.dataset.context === 'product');
      this.button.classList.toggle('st-1', signedIn && this.dataset.context === 'product');
      this.button.classList.toggle('iWishAddColl', signedIn && this.dataset.context !== 'product');
      this.button.classList.toggle('iwishcheck', signedIn && this.dataset.context !== 'product');
      this.syncIdentity();
      this.clickHandler = (event) => this.handleClick(event);
      this.keyHandler = (event) => {
        if (event.key === ' ') { event.preventDefault(); this.button.click(); }
        if (event.key === 'Escape') this.fallback.hidden = true;
      };
      this.button.addEventListener('click', this.clickHandler, true);
      this.button.addEventListener('keydown', this.keyHandler);
      this.observer = new MutationObserver(() => { this.providerStateUncertain = false; this.syncProviderState(); });
      this.observer.observe(this.button, {attributes:true, attributeFilter:['class', 'data-iwclick']});
      this.syncProviderState();
      if (this.productInfo && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeOptions = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({data}) => {
          if (!data?.event?.target || !this.productInfo.contains(data.event.target)) return;
          this.pending = true;
          if (this.dataset.customerSignedIn === 'true') this.button.setAttribute('aria-disabled', 'true');
          this.button.removeAttribute('aria-pressed');
          this.fallback.hidden = true;
        });
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
          if (String(data?.sectionId) !== String(this.productInfo.dataset.section)) return;
          this.pending = false;
          this.dataset.variantId = data.variant?.id ? String(data.variant.id) : '';
          this.dataset.variantTitle = data.variant?.title || '';
          this.syncIdentity();
          this.syncProviderState();
        });
      }
      if (signedIn && this.dataset.context !== 'product') queueMarameWishlistRefresh();
    }
    disconnectedCallback() {
      this.unsubscribeOptions?.();
      this.unsubscribeVariant?.();
      this.observer?.disconnect();
      this.button?.removeEventListener('click', this.clickHandler, true);
      this.button?.removeEventListener('keydown', this.keyHandler);
      this.initialized = false;
    }
    syncIdentity() {
      const form = this.dataset.context === 'product' && this.dataset.productFormId
        ? document.getElementById(this.dataset.productFormId) : null;
      const input = form?.querySelector('[name="id"]');
      const variantId = input ? input.value : this.dataset.variantId;
      this.variantValid = /^\d+$/.test(String(variantId || '')) && /^\d+$/.test(String(this.dataset.productId || ''));
      if (this.lastVariantId && this.lastVariantId !== String(variantId || '')) this.providerStateUncertain = true;
      this.lastVariantId = String(variantId || '');
      this.button.setAttribute('data-variant', this.variantValid ? String(variantId) : '');
      this.button.setAttribute('data-product', this.dataset.productId || '');
      this.button.setAttribute('data-ptitle', this.dataset.productTitle || '');
      this.button.setAttribute('aria-label', [this.dataset.label, this.dataset.productTitle, this.dataset.variantTitle === 'Default Title' ? '' : this.dataset.variantTitle].filter(Boolean).join(': '));
      if (this.dataset.customerSignedIn === 'true' && (this.pending || !this.variantValid)) this.button.setAttribute('aria-disabled', 'true');
      else this.button.removeAttribute('aria-disabled');
    }
    syncProviderState() {
      if (this.dataset.customerSignedIn !== 'true' || this.pending || !this.variantValid || this.providerStateUncertain || this.button.getAttribute('data-iwclick') !== 'true') {
        this.button.removeAttribute('aria-pressed');
        this.removeAttribute('data-provider-state');
        return;
      }
      const saved = this.button.classList.contains('iwishAdded');
      this.button.setAttribute('aria-pressed', String(saved));
      this.setAttribute('data-provider-state', saved ? 'saved' : 'unsaved');
    }
    handleClick(event) {
      if (this.dataset.customerSignedIn !== 'true') {
        event.stopImmediatePropagation();
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        this.fallback.hidden = true;
        const account = document.querySelector('marame-account');
        const opened = typeof account?.open === 'function' && account.open('wishlist', this.button);
        if (!opened && this.dataset.signInUrl) window.location.assign(this.dataset.signInUrl);
        return;
      }
      this.syncIdentity();
      const initialized = this.button.getAttribute('data-iwclick') === 'true';
      if (this.pending || !this.variantValid || !initialized) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.feedback.textContent = this.pending || !this.variantValid ? this.dataset.selectionLabel : this.dataset.unavailableLabel;
        this.fallback.hidden = false;
        return;
      }
      this.fallback.hidden = true;
    }
  });
}
